import React, {
  createContext,
  Dispatch,
  Reducer,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { CompanyDetails } from '@data/types/company';
import companyApi from '@data/api/company';
import { handleClearCompany, handleSetCompany, reducer } from './reducers';
import { CompanyAction } from './actions';
import { Empty } from '@src/@data/utility/NotImplemented';
import { logOnError } from '@data/utility/errors';
import { useInternationalization } from '@src/@data/store/InternationalizationProvider';

interface ContextValueType {
  selectedCompanyId?: string;
  selectedCompany?: CompanyDetails;
  isLoading?: boolean;
  selectCompany: (companyId: string) => void;
  dispatch: Dispatch<CompanyAction>;
  resetCompany: () => void;
}

export const CompanyContext = createContext<ContextValueType>({
  selectCompany: Empty,
  dispatch: Empty,
  resetCompany: Empty,
});

export const useCompany = () => useContext(CompanyContext);

interface Props {
  children: JSX.Element | JSX.Element[];
}

export const CompanyProvider: React.FC<Props> = ({ children }) => {
  const [selectedCompany, dispatch] = useReducer<
    Reducer<CompanyDetails | undefined, CompanyAction>
  >(reducer, undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const { setTimeZone } = useInternationalization();

  const selectCompany = useCallback((companyId: string) => {
    setCompanyId(companyId);
  }, []);

  const resetCompany = useCallback(() => {
    setCompanyId('');
    handleClearCompany(dispatch);
  }, []);

  useEffect(() => {
    if (!companyId) return;
    setIsLoading(true);
    handleClearCompany(dispatch);
    companyApi
      .getCompanyDetails(companyId)
      .then((company) => {
        handleSetCompany(dispatch, company);
        setTimeZone(company.timeZone);
        setIsLoading(false);
      })
      .catch((e) => {
        logOnError(e);
        setIsLoading(false);
      });
  }, [companyId, setTimeZone]);

  return (
    <CompanyContext.Provider
      value={{
        selectedCompanyId: companyId,
        selectedCompany,
        isLoading,
        selectCompany,
        dispatch,
        resetCompany,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};
